import { AxiosError } from 'axios';
import { toast } from '@/hooks/use-toast';
import {
  AUTH_TOKEN_KEY,
  AUTH_USER_KEY,
  SESSION_ACCESS_KEY,
  SESSION_REFRESH_KEY,
} from '@/config/constants';

/**
 * Global API Error Handler
 * Centralized error handling for all API requests
 * Provides smart error detection and user-friendly messages
 */

export interface ApiErrorResponse {
  data: null;
  status: false;
  error: string;
  detail: string | Record<string, string[]>;
}

export class ApiErrorHandler {
  /**
   * Handle API errors globally
   * @param error - The error from React Query or Axios
   * @param context - Additional context for debugging
   */
  static handle(error: unknown, context?: string): void {
    console.error(`API Error${context ? ` in ${context}` : ''}:`, error);

    // Check if it's a network error (server down)
    if (this.isNetworkError(error)) {
      this.handleNetworkError();
      return;
    }

    // Check if it's an authentication error
    if (this.isAuthError(error)) {
      this.handleAuthError();
      return;
    }

    // Check if it's a validation error
    if (this.isValidationError(error)) {
      this.handleValidationError(error as AxiosError<ApiErrorResponse>);
      return;
    }

    // Check if it's a server error
    if (this.isServerError(error)) {
      this.handleServerError(error as AxiosError<ApiErrorResponse>);
      return;
    }

    // Default error handling
    this.handleGenericError(error);
  }

  /**
   * Check if error is a network/connection error
   */
  private static isNetworkError(error: unknown): boolean {
    if (error instanceof AxiosError) {
      return (
        error.code === 'ERR_NETWORK' ||
        error.code === 'ERR_CONNECTION_REFUSED' ||
        error.message.includes('Network Error') ||
        !error.response // No response means network issue
      );
    }

    if (error instanceof Error) {
      return (
        error.message.includes('fetch') ||
        error.message.includes('Network') ||
        error.message.includes('Failed to fetch')
      );
    }

    return false;
  }

  /**
   * Check if error is authentication related
   */
  private static isAuthError(error: unknown): boolean {
    if (error instanceof AxiosError) {
      return error.response?.status === 401 || error.response?.status === 403;
    }
    return false;
  }

  /**
   * Check if error is validation related
   */
  private static isValidationError(error: unknown): boolean {
    if (error instanceof AxiosError) {
      return error.response?.status === 422;
    }
    return false;
  }

  /**
   * Check if error is server related
   */
  private static isServerError(error: unknown): boolean {
    if (error instanceof AxiosError) {
      const status = error.response?.status;
      return status ? status >= 500 && status < 600 : false;
    }
    return false;
  }

  /**
   * Handle network errors (server down, no connection)
   */
  private static handleNetworkError(): void {
    // Don't show toast for network errors - ErrorBoundary will handle this
    // This prevents multiple error messages for the same issue
    console.error('Network error detected - handled by ErrorBoundary');

    // Throw error to be caught by ErrorBoundary
    throw new Error('Network connection failed - server may be down');
  }

  /**
   * Handle authentication errors
   */
  private static handleAuthError(): void {
    toast({
      title: 'Authentication Required',
      description: 'Your session has expired. Please log in again.',
      variant: 'destructive',
    });

    // Clear auth data using correct storage keys
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
    sessionStorage.removeItem(SESSION_ACCESS_KEY);
    sessionStorage.removeItem(SESSION_REFRESH_KEY);

    // Dispatch custom event for AuthProvider to handle
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('auth:expired'));
      // Fallback redirect if event handler doesn't work
      setTimeout(() => {
        window.location.href = '/login';
      }, 100);
    }
  }

  /**
   * Handle validation errors
   */
  private static handleValidationError(
    error: AxiosError<ApiErrorResponse>
  ): void {
    const errorData = error.response?.data;

    if (errorData?.detail && typeof errorData.detail === 'object') {
      // Handle Laravel validation errors
      const validationErrors = Object.values(errorData.detail).flat();
      toast({
        title: 'Validation Error',
        description: validationErrors.join(', '),
        variant: 'destructive',
      });
    } else {
      toast({
        title: 'Validation Error',
        description:
          errorData?.error || 'Please check your input and try again.',
        variant: 'destructive',
      });
    }
  }

  /**
   * Handle server errors (5xx)
   */
  private static handleServerError(error: AxiosError<ApiErrorResponse>): void {
    const status = error.response?.status;
    const errorData = error.response?.data;

    let title = 'Server Error';
    let description =
      'An unexpected server error occurred. Please try again later.';

    switch (status) {
      case 500:
        title = 'Internal Server Error';
        description =
          'The server encountered an error. Our team has been notified.';
        break;
      case 502:
        title = 'Bad Gateway';
        description =
          'Server is temporarily unavailable. Please try again in a moment.';
        break;
      case 503:
        title = 'Service Unavailable';
        description = 'Service is temporarily down for maintenance.';
        break;
      case 504:
        title = 'Gateway Timeout';
        description = 'Server response timed out. Please try again.';
        break;
    }

    toast({
      title,
      description: errorData?.error || description,
      variant: 'destructive',
    });
  }

  /**
   * Handle generic errors
   */
  private static handleGenericError(error: unknown): void {
    let message = 'An unexpected error occurred. Please try again.';

    if (error instanceof Error) {
      message = error.message;
    } else if (typeof error === 'string') {
      message = error;
    }

    toast({
      title: 'Error',
      description: message,
      variant: 'destructive',
    });
  }

  /**
   * Create a React Query error handler
   */
  static createQueryErrorHandler(context?: string) {
    return (error: unknown) => {
      this.handle(error, context);
    };
  }
}

/**
 * Global error handler for React Query
 */
export const globalQueryErrorHandler = (error: unknown) => {
  ApiErrorHandler.handle(error, 'React Query');
};

export default ApiErrorHandler;
